import { type IntlShape } from 'react-intl';

export const formatFullDateLabel = (date: Date, intl: IntlShape) =>
  intl.formatDate(date, { month: 'short', day: '2-digit', year: '2-digit' });

export const toUtcDayKey = (date: Date): string => {
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  return `${date.getUTCFullYear()}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`;
};

export type DateGranularity = 'day' | 'week' | 'month' | 'year';

export interface ChartSeriesPoint {
  x: string;
  y: number;
  created: number;
  label: string;
  shortLabel: string;
  [key: string]: unknown;
}

interface BucketData {
  count: number;
  minDate: number;
  maxDate: number;
}

function getISOWeekUTC(date: Date): number {
  const dayOfWeek = date.getUTCDay() || 7;
  const thursdayOfWeek = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + (4 - dayOfWeek))
  );
  const yearStart = Date.UTC(thursdayOfWeek.getUTCFullYear(), 0, 1);
  const weekNumber = Math.ceil((thursdayOfWeek.getTime() - yearStart) / 86_400_000 / 7);
  return weekNumber;
}

function getISOWeekYearUTC(date: Date): number {
  const dayOfWeek = date.getUTCDay() || 7;
  const thursdayOfWeek = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + (4 - dayOfWeek))
  );
  return thursdayOfWeek.getUTCFullYear();
}

export function toUtcWeekKey(date: Date): string {
  const year = getISOWeekYearUTC(date);
  const week = getISOWeekUTC(date);
  return `${year}-W${week < 10 ? '0' : ''}${week}`;
}

export function toUtcMonthKey(date: Date): string {
  const month = date.getUTCMonth() + 1;
  return `${date.getUTCFullYear()}-${month < 10 ? '0' : ''}${month}`;
}

export function toUtcYearKey(date: Date): string {
  return String(date.getUTCFullYear());
}

function getStartOfWeekUTC(date: Date): number {
  const dayOfWeek = date.getUTCDay() || 7; // Monday=1, Sunday=7
  const monday = new Date(date);
  monday.setUTCDate(date.getUTCDate() - dayOfWeek + 1);
  return Date.UTC(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate());
}

// Label formatting for tooltip - "13 May - 19 May, 2025" or "28 Dec, 2025 - 4 Jan, 2026"
function formatPeriodLabel(minDate: number, maxDate: number, granularity: DateGranularity, intl: IntlShape): string {
  const minD = new Date(minDate);
  const maxD = new Date(maxDate);

  switch (granularity) {
    case 'day':
      return formatFullDateLabel(minD, intl);

    case 'week': {
      const sameYear = minD.getUTCFullYear() === maxD.getUTCFullYear();
      if (sameYear) {
        const start = intl.formatDate(minD, { day: 'numeric', month: 'short', timeZone: 'UTC' });
        const end = intl.formatDate(maxD, { day: 'numeric', month: 'short', timeZone: 'UTC' });
        const year = minD.getUTCFullYear();
        return `${start} - ${end}, ${year}`;
      }
      const start = intl.formatDate(minD, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
      const end = intl.formatDate(maxD, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
      return `${start} - ${end}`;
    }

    case 'month':
      return intl.formatDate(minD, { month: 'long', year: 'numeric', timeZone: 'UTC' });

    case 'year':
      return String(minD.getUTCFullYear());

    default:
      return '';
  }
}

// Short label formatting for axis ticks - "18 Mar-24 Mar"
function formatPeriodLabelShort(
  minDate: number,
  maxDate: number,
  granularity: DateGranularity,
  intl: IntlShape
): string {
  const minD = new Date(minDate);
  const maxD = new Date(maxDate);

  switch (granularity) {
    case 'day':
      return formatFullDateLabel(minD, intl);

    case 'week': {
      const start = intl.formatDate(minD, { day: 'numeric', month: 'short', timeZone: 'UTC' });
      const end = intl.formatDate(maxD, { day: 'numeric', month: 'short', timeZone: 'UTC' });
      return `${start}-${end}`;
    }

    case 'month':
      return intl.formatDate(minD, { month: 'short', year: 'numeric', timeZone: 'UTC' });

    case 'year':
      return String(minD.getUTCFullYear());

    default:
      return '';
  }
}

export function aggregateToChartSeries(
  dailyStats: Array<{ created?: number | null; total?: number | null }>,
  granularity: DateGranularity,
  intl: IntlShape
): ChartSeriesPoint[] {
  const validStats = dailyStats
    .filter((stat) => stat.created !== null && stat.total !== null)
    .sort((a, b) => (a.created as number) - (b.created as number));

  if (validStats.length === 0) {
    return [];
  }

  const getKey = (date: Date): string => {
    switch (granularity) {
      case 'day':
        return toUtcDayKey(date);
      case 'week':
        return toUtcWeekKey(date);
      case 'month':
        return toUtcMonthKey(date);
      case 'year':
        return toUtcYearKey(date);
      default:
        return toUtcDayKey(date);
    }
  };

  const buckets = new Map<string, BucketData>();
  validStats.forEach(({ created, total }) => {
    const timestamp = created as number;
    const date = new Date(timestamp);
    const key = getKey(date);

    const existing = buckets.get(key);
    if (existing) {
      existing.count += total ?? 0;
      existing.minDate = Math.min(existing.minDate, timestamp);
      existing.maxDate = Math.max(existing.maxDate, timestamp);
    } else {
      buckets.set(key, {
        count: total ?? 0,
        minDate: timestamp,
        maxDate: timestamp
      });
    }
  });

  const firstDate = new Date(validStats[0].created as number);
  const lastDate = new Date(validStats[validStats.length - 1].created as number);

  const series: ChartSeriesPoint[] = [];

  switch (granularity) {
    case 'day': {
      const startUtc = Date.UTC(firstDate.getUTCFullYear(), firstDate.getUTCMonth(), firstDate.getUTCDate());
      const endUtc = Date.UTC(lastDate.getUTCFullYear(), lastDate.getUTCMonth(), lastDate.getUTCDate());

      for (let current = startUtc; current <= endUtc; current += 86_400_000) {
        const currentDate = new Date(current);
        const key = getKey(currentDate);
        const bucket = buckets.get(key);

        series.push({
          x: String(series.length),
          y: bucket?.count ?? 0,
          created: bucket?.minDate ?? current,
          label: formatPeriodLabel(bucket?.minDate ?? current, bucket?.maxDate ?? current, granularity, intl),
          shortLabel: formatPeriodLabelShort(bucket?.minDate ?? current, bucket?.maxDate ?? current, granularity, intl)
        });
      }

      break;
    }
    case 'week': {
      let current = getStartOfWeekUTC(firstDate);
      const endUtc = Date.UTC(lastDate.getUTCFullYear(), lastDate.getUTCMonth(), lastDate.getUTCDate());

      while (current <= endUtc) {
        const currentDate = new Date(current);
        const key = getKey(currentDate);
        const bucket = buckets.get(key);

        const weekEnd = current + 6 * 86_400_000; // Sunday

        series.push({
          x: String(series.length),
          y: bucket?.count ?? 0,
          created: bucket?.minDate ?? current,
          label: formatPeriodLabel(bucket?.minDate ?? current, bucket?.maxDate ?? weekEnd, granularity, intl),
          shortLabel: formatPeriodLabelShort(bucket?.minDate ?? current, bucket?.maxDate ?? weekEnd, granularity, intl)
        });

        current += 7 * 86_400_000; // Next Monday
      }

      break;
    }
    case 'month': {
      let year = firstDate.getUTCFullYear();
      let month = firstDate.getUTCMonth();
      const endYear = lastDate.getUTCFullYear();
      const endMonth = lastDate.getUTCMonth();

      while (year < endYear || (year === endYear && month <= endMonth)) {
        const current = Date.UTC(year, month, 1);
        const currentDate = new Date(current);
        const key = getKey(currentDate);
        const bucket = buckets.get(key);

        const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
        const monthEnd = Date.UTC(year, month, lastDay);

        series.push({
          x: String(series.length),
          y: bucket?.count ?? 0,
          created: bucket?.minDate ?? current,
          label: formatPeriodLabel(bucket?.minDate ?? current, bucket?.maxDate ?? monthEnd, granularity, intl),
          shortLabel: formatPeriodLabelShort(bucket?.minDate ?? current, bucket?.maxDate ?? monthEnd, granularity, intl)
        });

        month++;
        if (month > 11) {
          month = 0;
          year++;
        }
      }

      break;
    }
    case 'year': {
      const startYear = firstDate.getUTCFullYear();
      const endYear = lastDate.getUTCFullYear();

      for (let year = startYear; year <= endYear; year++) {
        const current = Date.UTC(year, 0, 1);
        const currentDate = new Date(current);
        const key = getKey(currentDate);
        const bucket = buckets.get(key);

        const yearEnd = Date.UTC(year, 11, 31);

        series.push({
          x: String(series.length),
          y: bucket?.count ?? 0,
          created: bucket?.minDate ?? current,
          label: formatPeriodLabel(bucket?.minDate ?? current, bucket?.maxDate ?? yearEnd, granularity, intl),
          shortLabel: formatPeriodLabelShort(bucket?.minDate ?? current, bucket?.maxDate ?? yearEnd, granularity, intl)
        });
      }

      break;
    }
    // No default
  }

  return series;
}
